import PropTypes from 'prop-types';
import React, { useRef, useEffect, useMemo, createContext } from 'react';
import ModalExtended from '../bootstrap/Modal.js';
import { modalFocusHandler } from './modalHelpers.js';
import { jsx as _jsx } from "react/jsx-runtime";
export const ModalContext = /*#__PURE__*/createContext({
  onHide: () => {}
});

// Wrap in IIFE so we can set Modal.Body, etc without creating side effects.  Ugly but maintains react-bootstrap pattern
const Modal = /* #__PURE__*/(() => {
  function _Modal(_ref) {
    let {
      bsSize,
      bsStyle = 'default',
      onExit = () => {},
      onEntered = () => {},
      eventId = '',
      children,
      trapFocus = false,
      show,
      onHide,
      ...rest
    } = _ref;
    const modalRef = useRef(null);
    const modalContext = useMemo(() => ({
      onHide
    }), [onHide]);
    const sendCustomEvent = function (eventType) {
      if (eventType === void 0) {
        eventType = '';
      }
      if (eventId !== '') {
        const detail = {
          data: {
            eventType,
            eventId
          }
        };
        const eventName = eventType === 'show' ? 'modal:open' : 'modal:close';
        const customEvent = new CustomEvent(eventName, {
          detail
        });
        window.dispatchEvent(customEvent);
      }
    };
    const handleOnEntered = event => {
      sendCustomEvent('show');
      if (typeof onEntered === 'function') {
        onEntered(event);
      }
    };
    const handleOnExit = event => {
      sendCustomEvent('hide');
      if (typeof onExit === 'function') {
        onExit(event);
      }
    };
    useEffect(() => {
      if (show && trapFocus && modalRef?.current) {
        const {
          focusHandler,
          prevFocusableElement
        } = modalFocusHandler(modalRef.current);
        document.addEventListener('keydown', focusHandler, false);
        return () => {
          document.removeEventListener('keydown', focusHandler, false);
          prevFocusableElement.focus();
        };
      }
      return () => {};
    }, [show, trapFocus]);
    if (bsStyle === 'default') {
      bsStyle = '';
    }
    return /*#__PURE__*/_jsx(ModalContext.Provider, {
      value: modalContext,
      children: /*#__PURE__*/_jsx(ModalExtended, {
        "aria-labelledby": "modal-title",
        bsSize: bsSize,
        bsStyle: bsStyle,
        "data-cmp": "modal",
        onEntered: handleOnEntered,
        onExit: handleOnExit,
        show: show,
        onHide: onHide,
        autoFocus: false,
        ...rest,
        children: /*#__PURE__*/_jsx("div", {
          ref: modalRef,
          children: children
        })
      })
    });
  }
  _Modal.Body = ModalExtended.Body;
  _Modal.Header = ModalExtended.Header;
  _Modal.Title = ModalExtended.Title;
  _Modal.Footer = ModalExtended.Footer;
  _Modal.Dialog = ModalExtended.Dialog;
  return _Modal;
})();
export default Modal;